import React, { useState, useRef, useEffect } from "react";
import { Row, Col, Form, Spinner, Modal, Button } from "react-bootstrap";
import { FiUser, FiMail, FiPhone, FiMapPin, FiSave, FiEdit2, FiCamera } from "react-icons/fi";
import axios from "axios";

// Separate Header Component
const CustomerHeader = ({ customer, customerId, onCustomerUpdate, onShowSuccess, onShowError }) => {
    const [uploadingImage, setUploadingImage] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const fileInputRef = useRef(null);

    const handleImageChange = async (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                onShowError("Please select an image file");
                return;
            }

            if (file.size > 2 * 1024 * 1024) {
                onShowError("Image size should be less than 2MB");
                return;
            }

            setImagePreview(URL.createObjectURL(file));
            await uploadImage(file);
        }
    };

    const uploadImage = async (file) => {
        try {
            setUploadingImage(true);

            const AuthToken = localStorage.getItem("authToken");
            const formDataToSend = new FormData();
            formDataToSend.append('img', file);
            formDataToSend.append('_method', 'PUT');

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/customers-update/${customerId}`,
                formDataToSend,
                {
                    headers: {
                        Authorization: `Bearer ${AuthToken}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            if (response.data.success) {
                onShowSuccess("Profile image updated successfully");
                onCustomerUpdate();
            } else {
                onShowError(response.data.message || "Failed to upload image");
            }
        } catch (err) {
            console.error("Error uploading image:", err);
            onShowError(err.response?.data?.message || "Failed to upload image");
        } finally {
            setUploadingImage(false);
            setImagePreview(null);
        }
    };

    const getImageUrl = (imgPath) => {
        if (!imgPath) return null;
        if (imgPath.startsWith('http')) return imgPath;
        return `${process.env.REACT_APP_IMG_URL}${imgPath}`;
    };

    return (
        <div className="cp-photo-row">
            <div className="cp-pet-ident">
                <div className="cp-photo-wrap">
                    <div className="cp-photo">
                        {imagePreview ? (
                            <img src={imagePreview} alt="Preview" />
                        ) : customer.img ? (
                            <img
                                src={getImageUrl(customer.img)}
                                alt={customer.name}
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.style.display = 'none';
                                }}
                            />
                        ) : (
                            <FiUser size={30} />
                        )}
                    </div>

                    <button
                        type="button"
                        className="cp-photo-btn"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingImage}
                    >
                        {uploadingImage ? (
                            <Spinner as="span" animation="border" size="sm" />
                        ) : (
                            <FiCamera size={14} />
                        )}
                    </button>
                </div>

                <div>
                    <h4 className="cp-pet-name">{customer.name}</h4>
                    <div className="cp-pet-meta">
                        <span>Profile photo</span>
                        <span>JPG or PNG up to 2MB</span>
                    </div>
                </div>
            </div>

            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                style={{ display: 'none' }}
            />
        </div>
    );
};

const emptyForm = (customer) => ({
    name: customer?.name || '',
    email: customer?.email || '',
    phone: customer?.phone || '',
    address: customer?.address || '',
});

// Main CustomerInfoTab Component
const CustomerInfoTab = ({ customer, customerId, onCustomerUpdate, onShowSuccess, onShowError }) => {
    const [showEditModal, setShowEditModal] = useState(false);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState(emptyForm(customer));

    // The form is only a draft of the open modal, so it restarts from the saved
    // customer every time the modal opens.
    useEffect(() => {
        if (showEditModal) {
            setFormData(emptyForm(customer));
        }
    }, [showEditModal, customer]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleUpdate = async () => {
        try {
            setSaving(true);

            const AuthToken = localStorage.getItem("authToken");
            const formDataToSend = new FormData();
            formDataToSend.append('name', formData.name);
            formDataToSend.append('email', formData.email);
            formDataToSend.append('phone', formData.phone);
            formDataToSend.append('address', formData.address);
            formDataToSend.append('_method', 'PUT');

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/customers-update/${customerId}`,
                formDataToSend,
                {
                    headers: {
                        Authorization: `Bearer ${AuthToken}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            if (response.data.success) {
                onShowSuccess("Customer updated successfully");
                setShowEditModal(false);
                onCustomerUpdate();
            } else {
                onShowError(response.data.message || "Failed to update customer");
            }
        } catch (err) {
            console.error("Error updating customer:", err);
            if (err.response?.data?.errors) {
                const errors = err.response.data.errors;
                const errorMessages = Object.values(errors).flat().join(', ');
                onShowError(errorMessages);
            } else {
                onShowError(err.response?.data?.message || "Failed to update customer");
            }
        } finally {
            setSaving(false);
        }
    };

    const readOnlyField = (label, icon, value) => (
        <Col md={6}>
            <Form.Group className="mb-3">
                <Form.Label className="fw-bold">
                    {icon} {label}
                </Form.Label>
                <Form.Control
                    type="text"
                    value={value || 'N/A'}
                    disabled
                    readOnly
                    className="bg-light"
                />
            </Form.Group>
        </Col>
    );

    return (
        <>
            <div className="cp-section-head">
                <div>
                    <span className="cp-eyebrow">account details</span>
                    <h5 className="cp-section-title">Customer Information</h5>
                </div>

                <div className="cp-tools">
                    <button
                        type="button"
                        className="cp-ghost-btn is-solid"
                        onClick={() => setShowEditModal(true)}
                    >
                        <FiEdit2 size={13} /> Edit Profile
                    </button>
                </div>
            </div>

            <CustomerHeader
                customer={customer}
                customerId={customerId}
                onCustomerUpdate={onCustomerUpdate}
                onShowSuccess={onShowSuccess}
                onShowError={onShowError}
            />

            <div className="mt-4">
                <Row>
                    {readOnlyField('Full Name', <FiUser className="me-2" />, customer?.name)}
                    {readOnlyField('Email Address', <FiMail className="me-2" />, customer?.email)}
                </Row>

                <Row>
                    {readOnlyField('Phone Number', <FiPhone className="me-2" />, customer?.phone)}
                    {readOnlyField('Address', <FiMapPin className="me-2" />, customer?.address)}
                </Row>
            </div>

            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Customer</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bold">
                                    <FiUser className="me-2" /> Full Name <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    placeholder="Enter full name"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bold">
                                    <FiMail className="me-2" /> Email Address <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    placeholder="Enter email address"
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bold">
                                    <FiPhone className="me-2" /> Phone Number <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                    type="tel"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    placeholder="Enter phone number"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-bold">
                                    <FiMapPin className="me-2" /> Address
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    name="address"
                                    value={formData.address}
                                    onChange={handleInputChange}
                                    placeholder="Enter address"
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowEditModal(false)}
                        disabled={saving}
                    >
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleUpdate} disabled={saving}>
                        <FiSave size={13} className="me-1" />
                        {saving ? 'Saving...' : 'Update Customer'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default CustomerInfoTab;
